import { useState, type FormEvent } from "react";
import { asDecimalString } from "@shop-ledger/shared-types";
import { Button, DataTable, Input, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { createTaxRate, listTaxRates, updateTaxRate } from "../../api/taxRatesApi";
import type { TaxRateOut } from "../../api/types";
import { BranchSelect } from "../../components/BranchSelect";
import { StatusBanner } from "../../components/StatusBanner";
import { ApiError } from "../../lib/apiClient";
import { useAsyncData } from "../../lib/useAsyncData";
import { ROLES_WITH_TAX_FX_WRITE } from "../../roles/permissions";

/**
 * Tax rates — `GET/POST/PATCH/DELETE /tax-rates` (§13.1). A rate can be tenant-wide
 * (`branch_id` unset) or branch-specific; `is_inclusive` controls whether `retail_price`
 * already bakes in tax or has it added at sale time. Wired into the product form via
 * `TaxRateSelect` (`InventoryListPage`/`InventoryDetailPage`) — the applicable rate for a
 * sale line is resolved server-side (`app/services/tax_service.py`), never computed here.
 */
export function TaxRatesPage() {
  const { user, ensureFreshToken } = useAuth();
  const canWrite = user ? ROLES_WITH_TAX_FX_WRITE.includes(user.role) : false;
  const ratesState = useAsyncData(() => ensureFreshToken().then((token) => listTaxRates(token)), []);

  const [name, setName] = useState("");
  const [ratePercent, setRatePercent] = useState("");
  const [branchId, setBranchId] = useState("");
  const [isInclusive, setIsInclusive] = useState(false);
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [effectiveTo, setEffectiveTo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await createTaxRate(token, {
        name,
        rate_percent: asDecimalString(ratePercent),
        branch_id: branchId || undefined,
        is_inclusive: isInclusive,
        effective_from: effectiveFrom || undefined,
        effective_to: effectiveTo || undefined,
      });
      setName("");
      setRatePercent("");
      setBranchId("");
      setIsInclusive(false);
      setEffectiveFrom("");
      setEffectiveTo("");
      ratesState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not create tax rate.");
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleActive(rate: TaxRateOut) {
    try {
      const token = await ensureFreshToken();
      await updateTaxRate(token, rate.id, { is_active: !rate.is_active });
      ratesState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not update tax rate.");
    }
  }

  const columns: DataTableColumn<TaxRateOut, keyof TaxRateOut>[] = [
    { key: "name", header: "Name", render: (r) => r.name },
    { key: "rate_percent", header: "Rate", align: "right", render: (r) => `${r.rate_percent}%` },
    { key: "is_inclusive", header: "Pricing", render: (r) => (r.is_inclusive ? "Inclusive" : "Exclusive") },
    { key: "branch_id", header: "Scope", render: (r) => (r.branch_id ? <span className="muted">{r.branch_id.slice(0, 8)}</span> : "Tenant-wide default") },
    { key: "effective_from", header: "From", render: (r) => r.effective_from ?? "—" },
    { key: "effective_to", header: "To", render: (r) => r.effective_to ?? "—" },
    {
      key: "is_active",
      header: "Status",
      render: (r) => <span className={`badge ${r.is_active ? "badge--current" : "badge--overdue"}`}>{r.is_active ? "Active" : "Inactive"}</span>,
    },
    canWrite
      ? {
          key: "id",
          header: "",
          render: (r) => (
            <Button variant="ghost" size="md" type="button" onClick={() => void toggleActive(r)}>
              {r.is_active ? "Deactivate" : "Activate"}
            </Button>
          ),
        }
      : { key: "id", header: "", render: () => null },
  ];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Tax Rates</h1>
          <p className="page__subtitle">
            Jurisdiction/date-aware tax rates — tenant-wide or branch-specific, inclusive or exclusive
            pricing, optional effective date window.
          </p>
        </div>
      </div>

      {ratesState.error ? <StatusBanner kind="error">{ratesState.error}</StatusBanner> : null}
      {!canWrite && user ? (
        <StatusBanner kind="info">
          Your role ({user.role}) has read access to tax rates. Creating/editing is restricted to OWNER/ACCOUNTANT.
        </StatusBanner>
      ) : null}

      {canWrite ? (
        <div className="panel">
          <h2 className="panel__title">New tax rate</h2>
          <form onSubmit={handleCreate}>
            <div className="form-grid">
              <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. VAT Standard" required />
              <Input
                label="Rate %"
                inputMode="decimal"
                value={ratePercent}
                onChange={(e) => setRatePercent(e.target.value)}
                placeholder="e.g. 18"
                required
              />
              <BranchSelect id="tax-branch" label="Branch (blank = tenant-wide)" value={branchId} onChange={setBranchId} allOptionLabel="Tenant-wide default" />
              <label className="sl-field">
                <span className="sl-field__label">Inclusive pricing</span>
                <input type="checkbox" checked={isInclusive} onChange={(e) => setIsInclusive(e.target.checked)} />
              </label>
              <Input label="Effective from" type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
              <Input label="Effective to" type="date" value={effectiveTo} onChange={(e) => setEffectiveTo(e.target.value)} />
            </div>
            {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}
            <div className="form-actions">
              <Button type="submit" disabled={submitting}>
                {submitting ? "Creating…" : "Create tax rate"}
              </Button>
            </div>
          </form>
        </div>
      ) : null}

      <div className="panel">
        {ratesState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable columns={columns} rows={ratesState.data ?? []} getRowId={(row) => row.id} emptyMessage="No tax rates configured." caption="Tax Rates" />
        )}
      </div>
    </div>
  );
}
